import { useRef, useState } from 'react';

import { type Asset } from '../services/assets.product.search.service';
import { searchAssets } from '../services/CopProductCatalog.service';
import type {CategoryConfig} from "../CopProductCatalog";

type ProductCatalogSearchProps = {
  searchPlaceholder?: string;
  searchErrorMessage?: string;
  searchErrorGuidance?: string;
  categoriesJson?: CategoryConfig[];
};

const SearchIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#9ca3af"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const ProductCatalogSearch = ({ searchPlaceholder, searchErrorMessage, searchErrorGuidance, categoriesJson }: ProductCatalogSearchProps) => {
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [inputValue, setInputValue] = useState<string>('');
  const [searchResults, setSearchResults] = useState<Asset[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);

  const hasSearch = inputValue.trim().length > 0;

  const getAssetDetailUrl = (asset: Asset): string | null => {
    if (!categoriesJson?.length) {
      return null;
    }
    let matchedFamily: import('../CopProductCatalog').CategoryFamily | undefined;
    for (const category of categoriesJson) {
      if (category.families?.length) {
        // CategoryConfig[] structure: { categoryTitle, families: [...] }
        matchedFamily = category.families.find(
          (family) => family.familyTitle === asset.productCategoryName
        );
      } else {
        // Flat CategoryFamily[] structure: { familyTitle, familyLink, familyDetailLink }
        const flat = category as unknown as import('../CopProductCatalog').CategoryFamily;
        if (flat.familyTitle === asset.productCategoryName) {
          matchedFamily = flat;
        }
      }
      if (matchedFamily) break;
    }
    const detailLink = matchedFamily.familyDetailLink;
    if (!detailLink) {
      return null;
    }
    const query = asset.productCategoryId != null
      ? `?product-category-id=${asset.productCategoryId}`
      : '';
    return `${detailLink}/${asset.id}${query}`;
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(async () => {
      if (value.trim().length >= 3) {
        setIsSearching(true);
        const results = await searchAssets(value);
        setSearchResults(results);
        setIsSearching(false);
      } else {
        setSearchResults([]);
      }
    }, 300);
  };

  const handleClearSearch = () => {
    setInputValue('');
    setSearchResults([]);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
  };

  return (
    <div className="cop-product-catalog__search">
      <div className="cop-product-catalog__search-input-wrapper">
        <span className="cop-product-catalog__search-icon" aria-hidden="true">
          <SearchIcon />
        </span>
        <input
          type="search"
          value={inputValue}
          className="cop-product-catalog__search-input"
          placeholder={searchPlaceholder}
          onChange={handleSearchChange}
          aria-label={searchPlaceholder}
        />
        {hasSearch && (
          <button
            type="button"
            className="cop-product-catalog__search-clear"
            onClick={handleClearSearch}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {hasSearch && (
        <div className="cop-product-catalog__search-results">
          {isSearching && (
            <div className="cop-product-catalog__search-loading">Searching...</div>
          )}
          {!isSearching && searchResults.length > 0 && (
            <>
              <div className="cop-product-catalog__search-results-header">
                <span>Serial Number</span>
                <span>Asset ID</span>
                <span>Product Category</span>
              </div>
              <div className="cop-product-catalog__search-results-body">
                {searchResults.map((asset) => {
                  const detailUrl = getAssetDetailUrl(asset);
                  return (
                    <button
                      key={asset.id}
                      type="button"
                      className="cop-product-catalog__search-results-row"
                      onClick={() => { if (detailUrl) window.location.href = detailUrl; }}
                    >
                      <span className="cop-product-catalog__search-col--bold">{asset.serialNumber}</span>
                      <span>{asset.id}</span>
                      <span>{asset.productCategoryName}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
          {!isSearching && searchResults.length === 0 && (
            <div className="cop-product-catalog__search-empty">
              {searchErrorMessage && <p className="cop-product-catalog__search-empty-title">{searchErrorMessage}</p>}
              {searchErrorGuidance && <p className="cop-product-catalog__search-empty-subtitle">{searchErrorGuidance}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductCatalogSearch;
